import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import { FadeIn } from "@/components/motion";
import { TextField, AreaField, SelectField, SubmitButton, SuccessPanel } from "@/components/forms";
import { api, apiError } from "@/lib/api";
import { SITE } from "@/config/site";

const EMPTY = { name: "", email: "", phone: "", topic: "", message: "" };

const INFO = [
  { icon: Phone, label: "Phone", value: SITE.phone || "Details coming soon", href: SITE.phone ? SITE.phoneHref : null },
  { icon: Mail, label: "Email", value: SITE.email || "Details coming soon", href: SITE.email ? `mailto:${SITE.email}` : null },
  { icon: MapPin, label: "Office", value: SITE.address || "Details coming soon" },
  { icon: Clock, label: "Hours", value: SITE.hours || "Details coming soon" },
];

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/forms/contact", form);
      setSent(true);
      toast.success("Message sent — thank you for reaching out.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact Homevia"
        lines={["We're here to", "listen first."]}
        intro="Questions about care, referrals, careers, or anything else — send us a note and we'll follow up with you directly."
        testid="contact-heading"
      />

      <section className="px-6 pb-24 lg:px-10 lg:pb-32">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {sent ? (
              <SuccessPanel
                testid="contact-success"
                title="Message received"
                message="Thank you for reaching out. A member of the Homevia team will follow up with you."
                onReset={() => {
                  setForm(EMPTY);
                  setSent(false);
                }}
              />
            ) : (
              <FadeIn>
                <form
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  className="space-y-6 rounded-[2rem] border border-olive/10 bg-white p-8 shadow-[0_20px_60px_-30px_rgba(43,51,43,0.25)] sm:p-10"
                >
                  <div className="grid gap-6 sm:grid-cols-2">
                    <TextField id="contact-name" label="Your name" required value={form.name} onChange={set("name")} />
                    <TextField id="contact-email" label="Email" type="email" required value={form.email} onChange={set("email")} />
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <TextField id="contact-phone" label="Phone (optional)" type="tel" value={form.phone} onChange={set("phone")} />
                    <SelectField
                      id="contact-topic"
                      label="Topic"
                      value={form.topic}
                      onChange={set("topic")}
                      options={["General question", "Care for a loved one", "Referral partnership", "Careers", "Other"]}
                    />
                  </div>
                  <AreaField
                    id="contact-message"
                    label="Your message"
                    required
                    value={form.message}
                    onChange={set("message")}
                    placeholder="How can we help?"
                  />
                  <SubmitButton loading={loading} testid="contact-submit-btn">
                    Send Message
                  </SubmitButton>
                </form>
              </FadeIn>
            )}
          </div>

          <div className="lg:col-span-5">
            <FadeIn delay={0.15}>
              <div className="rounded-[2rem] border border-olive/10 bg-sand/60 p-8 lg:p-10">
                <h2 className="font-serif text-3xl text-ink">Reach us directly</h2>
                <p className="mt-3 text-sm leading-relaxed text-olive/70">
                  Call or email us during office hours — or send a note through the form anytime
                  and we'll follow up with you directly.
                </p>
                <div className="mt-8 space-y-5">
                  {INFO.map((item) => (
                    <div key={item.label} className="flex items-center gap-4">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-sage/20">
                        <item.icon className="h-5 w-5 text-teal-deep" />
                      </span>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal">{item.label}</p>
                        {item.href ? (
                          <a
                            href={item.href}
                            data-testid={`contact-${item.label.toLowerCase()}-link`}
                            className="text-sm font-semibold text-teal-deep transition-colors hover:text-olive hover:underline"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-sm text-olive/80">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
